import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { WorkbenchBreadcrumbConfig } from '@app/models/breadcrumb.model';
import { SubmittedInvoiceDto, SubmittedInvoiceFilterDto, SubmittedInvoiceJobType, SubmittedInvoiceStatus } from '@app/models/submitted-invoice.model';
import { InvoiceService } from '@app/services/invoice/invoice.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

interface InvoiceFilters {
  jobType: NonNullable<SubmittedInvoiceFilterDto['jobType']> | '';
  status: NonNullable<SubmittedInvoiceFilterDto['status']>;
  jobNumber: string;
  siteName: string;
  vendorInvoiceNumber: string;
  purchaseOrderNumber: string;
}

@Component({
  selector: 'app-submitted-invoices',
  templateUrl: './submitted-invoices.component.html',
  styleUrls: ['./submitted-invoices.component.scss'],
  standalone: false
})
export class SubmittedInvoicesComponent implements OnInit, OnDestroy {
  invoices: SubmittedInvoiceDto[] = [];
  readonly jobTypes = Object.values(SubmittedInvoiceJobType);
  readonly statuses = Object.values(SubmittedInvoiceStatus);
  totalCount = 0;
  private loadSubscription?: Subscription;
  filters: InvoiceFilters = this.defaultFilters();
  appliedFilters: InvoiceFilters = this.defaultFilters();
  pageIndex = 0;
  pageSize = 10;
  hasLoaded = false;
  hasLoadError = false;
  breadcrumb:WorkbenchBreadcrumbConfig;
  informationPanelDescription: string;

  constructor(
    private readonly invoiceService: InvoiceService,
    private readonly navigationService: NavigationService,
    private readonly translate: TranslateService
  ) {
    this.navigationService.onChange(false, false);
    this.breadcrumb = {
      title: this.translate.instant("resource_admin_dashboard_submitted_invoices"),
      breadcrumbItems: [
        {
          "label": this.translate.instant("resource_admin_dashboard_title"),
          "link": "/dashboard",
          "queryParams": {}
        },
        {
          "label": this.translate.instant("resource_admin_dashboard_submitted_invoices"),
          "link": "",
          "queryParams": {}
        },
      ]
    };
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - 3);
    this.informationPanelDescription = this.translate.instant("submitted_invoices_information_description").replace("{0}", cutoffDate.toLocaleDateString());
  }

  ngOnInit(): void {
    this.loadInvoices();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  get selectedStatusIndex(): number {
    return this.statuses.indexOf(this.appliedFilters.status);
  }

  get showIposInvoiceNumber(): boolean {
    return this.appliedFilters.status === SubmittedInvoiceStatus.Approved;
  }

  get showStatusColumn(): boolean {
    return this.appliedFilters.status === SubmittedInvoiceStatus.Rejected;
  }

  changeStatus(status: InvoiceFilters['status']): void {
    if (status === this.appliedFilters.status) {
      return;
    }

    this.filters.status = status;
    this.appliedFilters = { ...this.appliedFilters, status };
    this.pageIndex = 0;
    this.loadInvoices();
  }

  applyFilters(): void {
    this.appliedFilters = { ...this.filters };
    this.pageIndex = 0;
    this.loadInvoices();
  }

  resetFilters(): void {
    this.filters = this.defaultFilters();
    this.applyFilters();
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadInvoices();
  }

  private loadInvoices(): void {
    this.loadSubscription?.unsubscribe();
    this.hasLoaded = false;
    this.hasLoadError = false;
    this.loadSubscription = this.invoiceService.getSubmittedInvoicesByFilter({
      jobType: this.appliedFilters.jobType || null,
      status: this.appliedFilters.status,
      jobReferenceNumber: this.appliedFilters.jobNumber.trim() || null,
      siteName: this.appliedFilters.siteName.trim() || null,
      vendorInvoiceNumber: this.appliedFilters.vendorInvoiceNumber.trim() || null,
      purchaseOrderNumber: this.appliedFilters.purchaseOrderNumber.trim() || null,
      pageNumber: this.pageIndex + 1,
      pageSize: this.pageSize
    }).subscribe({
      next: page => {
        this.invoices = page.items;
        this.totalCount = page.totalCount;
        this.pageIndex = page.pageNumber - 1;
        this.pageSize = page.pageSize;
        this.hasLoaded = true;
      },
      error: () => {
        this.invoices = [];
        this.totalCount = 0;
        this.hasLoadError = true;
        this.hasLoaded = true;
      }
    });
  }

  private defaultFilters(): InvoiceFilters {
    return {
      jobType: SubmittedInvoiceJobType.Reactive,
      status: SubmittedInvoiceStatus.Submitted,
      jobNumber: '',
      siteName: '',
      vendorInvoiceNumber: '',
      purchaseOrderNumber: ''
    };
  }
}